import React from 'react';
import { Link } from 'react-router-dom';
import { LockClosedIcon, ArrowUpIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { motion } from 'framer-motion';
import { ROLES } from '../config/routeConfig';
import { teaserContent } from '../config/teaserContent';
import { TeaserErrorBoundary } from '../components/teaser-demos/TeaserErrorBoundary';

interface TeaserPageProps {
  featureName: string;
  minimumRequiredRole?: string; // Changed from requiredRoles
}

// Updated function to accept minimumRequiredRole and include ADMIN
const getMinimumPlanName = (role?: string): string => {
  if (role === ROLES.ADMIN) return 'Admin'; // Added Admin check
  if (role === ROLES.ELITE) return 'Elite';
  if (role === ROLES.STANDARD) return 'Standard';
  if (role === ROLES.BASIC) return 'Basic'; // Should ideally not show teaser for Basic if it's the minimum
  return 'a specific access level'; // Updated fallback
};

const TeaserPage: React.FC<TeaserPageProps> = ({ featureName, minimumRequiredRole }) => {
  // Use the updated function
  const minPlan = getMinimumPlanName(minimumRequiredRole);
  // Fetch base content
  const baseContent = teaserContent[featureName] || teaserContent['Default'];

  // Replace placeholders in title and description
  const content = {
    ...baseContent,
    title: baseContent.title
             .replace('{featureName}', featureName)
             .replace('{minPlan}', minPlan), // Replace {minPlan} placeholder
    description: baseContent.description
                   .replace('{featureName}', featureName) // Keep replacing featureName if needed
                   .replace('{minPlan}', minPlan) // Replace {minPlan} placeholder
  };

  // Animation variants similar to HomePage
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.6, 
        ease: "easeOut" 
      } 
    }
  };

  // Animation for scroll-triggered sections
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        duration: 0.6, 
        ease: "easeOut" 
      } 
    }
  };

  // Animation for individual feature items
  const featureItemVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  // Reverse animation for alternating layout
  const featureItemVariantsReverse = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 relative overflow-hidden">
      {/* Animated background blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10"
      >      {/* Introductory Section */}
      <motion.div
        variants={itemVariants}
        className="py-16 md:py-24 px-4 md:px-8 text-center bg-white/10 backdrop-blur-lg border border-white/20 mx-4 md:mx-8 mt-8 rounded-2xl shadow-xl"
      >
        <LockClosedIcon className="w-12 h-12 text-yellow-400 mb-4 inline-block" />        <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 text-transparent bg-clip-text leading-tight pb-1">
          {/* Use the processed title */}
          {content.title}
        </h1>
        <p className="text-lg md:text-xl mb-8 max-w-3xl mx-auto text-gray-200">
          {/* Use the processed description */}
          {content.description}        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transform transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-transparent"
        >
          <ArrowUpIcon className="w-5 h-5 mr-2" />
          {/* This button text is already dynamic */}
          Upgrade to {minPlan} & Unlock
        </Link>
      </motion.div>

      {/* Alternating Sub-Features Section */}
      <div className="space-y-16 md:space-y-24 py-12 md:py-20">
        {content.subFeatures.map((subFeature, index) => (
          <motion.section
            key={index}
            className="relative"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <div className="container mx-auto px-4 md:px-6 lg:px-8">
              <div className={`flex flex-col md:flex-row items-stretch gap-8 md:gap-12 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                {/* Image/Component Column */}
                <motion.div 
                  className="w-full md:w-1/2 lg:w-5/12 flex flex-col"
                  variants={index % 2 !== 0 ? featureItemVariantsReverse : featureItemVariants}
                >
                  {subFeature.component ? (
                    <div className="rounded-lg shadow-xl w-full overflow-hidden bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 border border-white/10 flex-1 min-h-[300px] md:min-h-[400px] flex">
                      <TeaserErrorBoundary
                        fallbackImageUrl={subFeature.imageUrl}
                        featureName={subFeature.title}
                      >
                        <React.Suspense 
                          fallback={
                            <motion.div
                              animate={{ opacity: [0.3, 0.7, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              className="w-full h-full bg-white/10 rounded flex items-center justify-center min-h-[300px]"
                            >
                              <span className="text-white/50 text-sm">Loading preview...</span>
                            </motion.div>
                          }
                        >
                          <subFeature.component 
                            {...subFeature.mockData}
                            className="w-full h-full flex-1"
                            aspectRatio={index % 2 === 0 ? 'video' : 'square'} // Responsive hint
                          />
                        </React.Suspense>
                      </TeaserErrorBoundary>
                    </div>
                  ) : (
                    <div className="rounded-lg shadow-xl w-full overflow-hidden flex-1 min-h-[300px] md:min-h-[400px] flex">
                      <img
                        src={subFeature.imageUrl}
                        alt={subFeature.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </motion.div>
                
                {/* Text Column */}
                <motion.div 
                  className="w-full md:w-1/2 lg:w-7/12 text-center md:text-left flex flex-col justify-center"
                  variants={index % 2 !== 0 ? featureItemVariants : featureItemVariantsReverse}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="mb-3"
                  >
                    {subFeature.icon ? (
                      <subFeature.icon className="w-8 h-8 text-blue-400 mx-auto md:mx-0" />
                    ) : (
                      <CheckCircleIcon className="w-8 h-8 text-green-400 mx-auto md:mx-0" />
                    )}
                  </motion.div>
                  <motion.h3 
                    className="text-2xl md:text-3xl font-semibold mb-4 text-white"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    {subFeature.title}
                  </motion.h3>
                  <motion.p 
                    className="text-base md:text-lg text-gray-200 leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    {subFeature.description.replace('{minPlan}', minPlan)}
                  </motion.p>
                </motion.div>
              </div>
            </div>
          </motion.section>
        ))}
      </div>      {/* Final Upgrade Button Section */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeInUp}
        className="py-16 md:py-20 text-center bg-white/10 backdrop-blur-lg border border-white/20 mx-4 md:mx-8 mb-8 rounded-2xl shadow-xl"
      >
        <h2 className="text-3xl font-bold mb-6 text-white">Ready to Upgrade?</h2>
        {/* This paragraph text is already dynamic */}
        <p className="text-lg mb-8 text-gray-200">Unlock {featureName} and more by upgrading to the {minPlan} plan.</p>        <Link
          to="/pricing"
          className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transform transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-transparent"
        >
          <ArrowUpIcon className="w-5 h-5 mr-2" /> {/* Adjusted icon size */}
          View Pricing & Upgrade Now</Link>
      </motion.div>
      </motion.div>
    </div>
  );
};

export default TeaserPage;